import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/apiClient'
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'
import AdminOKXOrdersTab from '@/components/okx/AdminOKXOrdersTab'

interface Deposit {
  id: string
  user_id: string
  user_name: string
  user_email: string
  amount: number
  fee: number
  real_amount: number
  bonus_amount: number
  bonus_rate: number
  payment_method: string
  status: string
  created_at: string
}

export default function DepositsPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'user-deposits' | 'operator-deposits' | 'okx'>('user-deposits')
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    if (activeTab !== 'okx') {
      fetchDeposits()
    }
  }, [currentPage, searchTerm, statusFilter, activeTab])

  const fetchDeposits = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        // 根据标签页过滤订单类型
        order_type: activeTab === 'user-deposits' ? '1' : '2',
      })
      
      const response = await apiClient.get('/admin-deposits', Object.fromEntries(params))
      if (response.success && response.data) {
        setDeposits((response.data as any).deposits || [])
        setTotal((response.data as any).pagination?.total || 0)
      }
    } catch (error) {
      console.error('Failed to fetch deposits:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; label: string }> = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '待支付' },
      'processing': { bg: 'bg-blue-100', text: 'text-blue-800', label: '处理中' },
      'completed': { bg: 'bg-green-100', text: 'text-green-800', label: '已完成' },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', label: '失败' },
    }
    const config = statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  return (
    <div className="page-content">
      <div className="mb-8">
        <h1 className="page-title">充值订单管理</h1>
        <p className="page-subtitle">管理用户充值订单和 OKX 订单</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('user-deposits')
                setCurrentPage(1)
              }}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'user-deposits'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              用户充值
            </button>
            <button
              onClick={() => {
                setActiveTab('operator-deposits')
                setCurrentPage(1)
              }}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'operator-deposits'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              运营商充值
            </button>
            <button
              onClick={() => setActiveTab('okx')}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'okx'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              OKX 订单
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'okx' ? (
        <AdminOKXOrdersTab />
      ) : (
        <>
          {/* Filters */}
          <div className="card mb-6">
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={activeTab === 'user-deposits' ? '搜索用户邮箱或订单ID...' : '搜索运营商名称或订单ID...'}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="form-input pl-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="form-input"
                  >
                    <option value="all">所有状态</option>
                    <option value="pending">待支付</option>
                    <option value="processing">处理中</option>
                    <option value="completed">已完成</option>
                    <option value="failed">失败</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">订单ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {activeTab === 'user-deposits' ? '用户' : '运营商'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">金额</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">手续费</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">实际到账</th>
                    {activeTab === 'user-deposits' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">赠金</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">支付方式</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">时间</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={activeTab === 'user-deposits' ? 9 : 8} className="px-6 py-4 text-center text-gray-500">
                        加载中...
                      </td>
                    </tr>
                  ) : !deposits || deposits.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'user-deposits' ? 9 : 8} className="px-6 py-4 text-center text-gray-500">
                        暂无数据
                      </td>
                    </tr>
                  ) : (
                    deposits.map((deposit) => (
                      <tr key={deposit.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/deposits/${deposit.id}`)}>
                        <td className="px-6 py-4 text-sm text-gray-900 font-mono">{deposit.id.substring(0, 8)}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="text-gray-900">{deposit.user_name}</div>
                          <div className="text-xs text-gray-500">{deposit.user_email}</div>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatCurrency(deposit.amount)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{formatCurrency(deposit.fee)}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-600">{formatCurrency(deposit.real_amount)}</td>
                        {activeTab === 'user-deposits' && (
                          <td className="px-6 py-4 text-sm">
                            <div className="text-green-600 font-medium">{formatCurrency(deposit.bonus_amount)}</div>
                            {deposit.bonus_rate > 0 && (
                              <div className="text-xs text-gray-500">{(deposit.bonus_rate * 100).toFixed(1)}%</div>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-gray-600">{deposit.payment_method}</td>
                        <td className="px-6 py-4 text-sm">{getStatusBadge(deposit.status)}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(deposit.created_at).toLocaleDateString('zh-CN')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                共 {total} 条记录
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  上一页
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage * 20 >= total}
                  className="btn-secondary"
                >
                  下一页
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
